import React, { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useRelayEnvironment } from 'react-relay/hooks';
import { fetchQuery } from 'relay-runtime';
import { Checkbox } from 'src/components/widgets/checkbox';
import { Radio } from 'src/components/widgets/radio';
import { T, toGlobalID, toLocalID } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';
import { TOOL_DEFINITIONS } from '../common/constants';
import { PlanUpgradeBanner } from '../common/planUpgradeBanner';
import { FormDivider, FormLabelCompact, SectionCard, SectionCardTitle, StackedBody, StackedBreadcrumb } from '../common/styled';
import type { bulkVariationEditor_AttributesQuery } from './__generated__/bulkVariationEditor_AttributesQuery.graphql';
import type { bulkVariationEditor_Query } from './__generated__/bulkVariationEditor_Query.graphql';
import type { bulkVariationEditor_UpdateMutation } from './__generated__/bulkVariationEditor_UpdateMutation.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

export interface BulkVariationEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
    onCanSaveChange?: (canSave: boolean) => void;
}

const CompactSelect = styled.select`
    padding: 4px 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 4px;
    font-size: 12px;
`;

const AttributeList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
`;

const InfoText = styled.div`
    font-size: 12px;
    color: #78909c;
`;

const RadioRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;

    > * {
        margin: 2px 0;
    }
`;

interface ParsedOptions {
    enabled?: boolean;
    showDropdownVersion?: boolean;
    showUnitPrice?: boolean;
    salesChannelSpecific?: { salesChannelID: string; attributeCodes: string[] }[];
}

const parseOptions = (opts: string | null): ParsedOptions => {
    if (opts && opts !== '') {
        try {
            return JSON.parse(opts);
        } catch {
            return {};
        }
    }
    return {};
};

const attributesQuery = graphql`
    query bulkVariationEditor_AttributesQuery($sku: String!, $salesChannelID: ID!) {
        merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
            optionID
            label
        }
    }
`;

// Nested, lazy-loaded query so the attribute checkboxes reload when the selected sales
// channel changes. Wrapping this in Suspense keeps the rest of the editor interactive.
const AttributeCheckboxes: FC<{
    sku: string;
    salesChannelID: string;
    selectedIds: string[];
    onToggle: (id: string) => void;
}> = ({ sku, salesChannelID, selectedIds, onToggle }) => {
    const data = useLazyLoadQuery<bulkVariationEditor_AttributesQuery>(attributesQuery, { sku, salesChannelID });
    const options = data?.merchantProductOptions ?? [];

    if (options.length === 0) {
        return <InfoText>{T._('No attributes available for this sales channel.', 'Admin')}</InfoText>;
    }

    return (
        <AttributeList>
            {options.map((opt) => (
                <Checkbox
                    key={opt.optionID}
                    checked={selectedIds.includes(opt.optionID)}
                    onChange={() => onToggle(opt.optionID)}
                    label={opt.label ?? opt.optionID}
                />
            ))}
        </AttributeList>
    );
};

const BulkVariationEditorContent: FC<BulkVariationEditorProps> = ({ productId, toolKey, onBack, onSave, saveRef, onCanSaveChange }) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const localProductId = String(toLocalID(productId) ?? '');
    const productGlobalId = toGlobalID(Number(localProductId), 'Model');
    const relayEnvironment = useRelayEnvironment();
    const { subscription } = useSubscriptionData();
    const isPlanSupported = subscription?.plan?.additionalFeatures.bulkVariationForm ?? false;
    useEffect(() => {
        onCanSaveChange?.(isPlanSupported);
    }, [isPlanSupported, onCanSaveChange]);

    const data = useLazyLoadQuery<bulkVariationEditor_Query>(
        graphql`
            query bulkVariationEditor_Query($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                        code
                        salesChannel {
                            id
                            detail {
                                properties {
                                    name
                                }
                            }
                            ecommerceSettings {
                                ecommerceName
                            }
                        }
                        additionalCodes {
                            code
                            salesChannel {
                                id
                                detail {
                                    properties {
                                        name
                                    }
                                }
                                ecommerceSettings {
                                    ecommerceName
                                }
                            }
                        }
                    }
                }
                productMultipleVariantsRule(productId: $id) {
                    attributeCodes
                    nameAndNumberEnabled
                    skipOptionForNameAndNumber
                    bulkAsNameAndNumberEnabled
                    enableImportCSV
                    options
                }
            }
        `,
        { id: productGlobalId },
        { fetchPolicy: 'network-only' }
    );

    const rule = data?.productMultipleVariantsRule;
    const model = data?.node && 'name' in (data.node as any) ? (data.node as any) : null;
    const productName = model?.name || '';
    const existingOptions = parseOptions(rule?.options ?? null);

    const productChannels = useMemo(() => {
        const list: { id: string; sku: string; name: string }[] = [];
        if (model?.salesChannel && model.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && model.code) {
            list.push({
                id: model.salesChannel.id,
                sku: model.code,
                name: model.salesChannel.detail?.properties?.name || model.salesChannel.ecommerceSettings?.ecommerceName || 'Store'
            });
        }
        if (model?.additionalCodes) {
            model.additionalCodes.forEach((ac: any) => {
                if (ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
                    list.push({
                        id: ac.salesChannel.id,
                        sku: ac.code,
                        name: ac.salesChannel.detail?.properties?.name || ac.salesChannel.ecommerceSettings?.ecommerceName || 'Store'
                    });
                }
            });
        }
        return list;
    }, [model?.salesChannel?.id, model?.code, model?.additionalCodes]);

    // Form Options state
    const [showDropdownVersion, setShowDropdownVersion] = useState(() => existingOptions.showDropdownVersion ?? false);
    const [showUnitPrice, setShowUnitPrice] = useState(() => existingOptions.showUnitPrice !== false);

    // Attributes state
    const [selectedSalesChannelId, setSelectedSalesChannelId] = useState<string>(productChannels[0]?.id ?? '');
    const [applyToAllChannels, setApplyToAllChannels] = useState(false);
    const [isApplyingToAllChannels, setIsApplyingToAllChannels] = useState(false);
    const [salesChannelAttributeCodes, setSalesChannelAttributeCodes] = useState<Record<string, string[]>>(() => {
        const initial: Record<string, string[]> = {};
        const mainChannelId = model?.salesChannel?.id;
        if (mainChannelId) {
            initial[mainChannelId] = [...(rule?.attributeCodes ?? [])];
        }
        if (existingOptions.salesChannelSpecific) {
            existingOptions.salesChannelSpecific.forEach((sc) => {
                initial[sc.salesChannelID] = [...sc.attributeCodes];
            });
        }
        return initial;
    });

    const selectedChannel = productChannels.find((c) => c.id === selectedSalesChannelId);
    const selectedAttributeCodes = selectedSalesChannelId ? salesChannelAttributeCodes[selectedSalesChannelId] || [] : [];

    const toggleAttribute = (id: string) => {
        if (!selectedSalesChannelId) return;
        setSalesChannelAttributeCodes((prev) => {
            const current = prev[selectedSalesChannelId] || [];
            const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
            return { ...prev, [selectedSalesChannelId]: next };
        });
    };

    const handleApplyToAllChannels = async (checked: boolean) => {
        setApplyToAllChannels(checked);
        if (checked) {
            setIsApplyingToAllChannels(true);
            const updated = { ...salesChannelAttributeCodes };
            for (const channel of productChannels) {
                try {
                    const optionsData = await fetchQuery<bulkVariationEditor_AttributesQuery>(relayEnvironment, attributesQuery, {
                        sku: channel.sku,
                        salesChannelID: channel.id
                    }).toPromise();
                    if (optionsData?.merchantProductOptions) {
                        updated[channel.id] = optionsData.merchantProductOptions.map((o) => o.optionID);
                    } else {
                        updated[channel.id] = [];
                    }
                } catch {
                    updated[channel.id] = updated[channel.id] || [];
                }
            }
            setSalesChannelAttributeCodes(updated);
            setIsApplyingToAllChannels(false);
        } else {
            const updated: Record<string, string[]> = {};
            for (const channel of productChannels) {
                if (channel.id === selectedSalesChannelId) {
                    updated[channel.id] = salesChannelAttributeCodes[channel.id] || [];
                } else {
                    updated[channel.id] = [];
                }
            }
            setSalesChannelAttributeCodes(updated);
        }
    };

    const [commitUpdate] = useMutation<bulkVariationEditor_UpdateMutation>(graphql`
        mutation bulkVariationEditor_UpdateMutation($id: ID!, $input: ProductMultipleVariantsRuleInput!) {
            updateProductMultipleVariantsRule(productId: $id, input: $input) {
                attributeCodes
                nameAndNumberEnabled
                skipOptionForNameAndNumber
                bulkAsNameAndNumberEnabled
                enableImportCSV
                options
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (!rule) return;
        const mainChannelId = model?.salesChannel?.id;
        const salesChannelSpecific: { salesChannelID: string; attributeCodes: string[] }[] = [];
        productChannels.forEach((ch) => {
            if (ch.id !== mainChannelId) {
                salesChannelSpecific.push({
                    salesChannelID: ch.id,
                    attributeCodes: [...(salesChannelAttributeCodes[ch.id] || [])]
                });
            }
        });

        const updatedOptions: ParsedOptions = {
            ...existingOptions,
            showDropdownVersion,
            showUnitPrice,
            enabled: true,
            salesChannelSpecific: salesChannelSpecific.length > 0 ? salesChannelSpecific : undefined
        };

        const finalAttributeCodes = mainChannelId ? salesChannelAttributeCodes[mainChannelId] || [] : [];
        await new Promise<void>((resolve, reject) => {
            commitUpdate({
                variables: {
                    id: productGlobalId,
                    input: {
                        attributeCodes: finalAttributeCodes,
                        nameAndNumberEnabled: rule.nameAndNumberEnabled,
                        skipOptionForNameAndNumber: rule.skipOptionForNameAndNumber,
                        bulkAsNameAndNumberEnabled: rule.bulkAsNameAndNumberEnabled,
                        enableImportCSV: rule.enableImportCSV,
                        options: JSON.stringify(updatedOptions)
                    }
                },
                onCompleted: () => {
                    onSave();
                    resolve();
                },
                onError: (error) => reject(error)
            });
        });
    }, [rule, model, productChannels, existingOptions, showDropdownVersion, showUnitPrice, salesChannelAttributeCodes, productGlobalId, onSave, commitUpdate]);

    saveRef.current = handleSave;

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName}
            </StackedBreadcrumb>
            <StackedBody>
                {!isPlanSupported && (
                    <PlanUpgradeBanner
                        message={T._(
                            'Your current plan does not include the Bulk Variation Form. This rule cannot be edited until you upgrade your plan.',
                            'Admin'
                        )}
                    />
                )}
                <SectionCard>
                    <SectionCardTitle>{T._('Attributes Selection', 'Admin')}</SectionCardTitle>

                    {productChannels.length === 0 ? (
                        <InfoText>{T._('Bulk Variation Form is not available: connect this product to an e-commerce sales channel first.', 'Admin')}</InfoText>
                    ) : (
                        <>
                            {productChannels.length > 1 && (
                                <>
                                    <FormDivider>
                                        <FormLabelCompact>{T._('Sales Channel', 'Admin')}</FormLabelCompact>
                                        <CompactSelect value={selectedSalesChannelId} onChange={(e) => setSelectedSalesChannelId(e.target.value)}>
                                            {productChannels.map((ch) => (
                                                <option key={ch.id} value={ch.id}>
                                                    {ch.name}
                                                </option>
                                            ))}
                                        </CompactSelect>
                                    </FormDivider>
                                    <Checkbox
                                        checked={applyToAllChannels}
                                        disabled={isApplyingToAllChannels}
                                        onChange={handleApplyToAllChannels}
                                        label={isApplyingToAllChannels ? T._('Fetching options...', 'Admin') : T._('Apply to all sales channels', 'Admin')}
                                    />
                                </>
                            )}
                            {selectedChannel && (
                                <Suspense fallback={<InfoText>{T._('Loading attributes...', 'Admin')}</InfoText>}>
                                    <AttributeCheckboxes
                                        sku={selectedChannel.sku}
                                        salesChannelID={selectedChannel.id}
                                        selectedIds={selectedAttributeCodes}
                                        onToggle={toggleAttribute}
                                    />
                                </Suspense>
                            )}
                        </>
                    )}
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('Form Options', 'Admin')}</SectionCardTitle>

                    <FormDivider>
                        <FormLabelCompact>{T._('Form version', 'Admin')}</FormLabelCompact>
                        <RadioRow>
                            <Radio
                                name='formVersion'
                                label={T._('Grid version', 'Admin')}
                                value='grid'
                                checked={!showDropdownVersion}
                                onSelect={() => setShowDropdownVersion(false)}
                            />
                            <Radio
                                name='formVersion'
                                label={T._('Dropdown menu version', 'Admin')}
                                value='dropdown'
                                checked={showDropdownVersion}
                                onSelect={() => setShowDropdownVersion(true)}
                            />
                        </RadioRow>
                    </FormDivider>

                    <FormDivider>
                        <Checkbox checked={showUnitPrice} onChange={() => setShowUnitPrice(!showUnitPrice)} label={T._('Show unit prices', 'Admin')} />
                    </FormDivider>
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const BulkVariationEditor: FC<BulkVariationEditorProps> = (props) => {
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <PanelLinesSkeleton />
                    </StackedBody>
                </>
            }
        >
            <BulkVariationEditorContent {...props} />
        </Suspense>
    );
};
